<template>
  <div class="col-md-12 mb-10 theme-customise">
    <div class="settings-page-head mb-5">
      <h4 class="card-label text-primary font-weight-bolder mb-1">Customise this theme</h4>
      <small class="text-muted">
        Banners and content below belong to the selected theme. Open Brand colours if you want to change them. Click Save changes to publish.
      </small>
    </div>

    <div class="theme-editor-card mb-5">
      <h5 class="mb-2">Homepage sections</h5>
      <div
        v-if="Number(template) === 12"
        class="form-group mb-4"
      >
        <label class="d-block mb-2">Discover strip (Fresh Products, Skilled Chefs, etc.)</label>
        <b-form-radio-group
          v-model="showDiscoverSection"
          :options="[
            { text: 'Show', value: '1' },
            { text: 'Hide', value: '0' }
          ]"
          buttons
          button-variant="outline-primary"
          size="sm"
        ></b-form-radio-group>
        <span class="form-text text-muted">
          Hide this if you do not want the four feature cards under the banner.
        </span>
      </div>
      <div class="form-group mb-0">
        <label class="d-block mb-2">Advertisement section</label>
        <b-form-radio-group
          v-model="showAdvertisementSection"
          :options="[
            { text: 'Show', value: '1' },
            { text: 'Hide', value: '0' }
          ]"
          buttons
          button-variant="outline-primary"
          size="sm"
        ></b-form-radio-group>
        <span class="form-text text-muted">
          Hide this if you do not want the advertisement / about block on the homepage.
        </span>
      </div>
      <button
        v-if="$hasPermission('manage business settings ') || $hasPermission('manage business settings')"
        type="button"
        class="btn btn-primary btn-sm mt-3"
        :disabled="savingHomepageSections"
        @click="saveHomepageSections"
      >
        {{ savingHomepageSections ? 'Saving…' : 'Save section visibility' }}
      </button>
    </div>

    <div v-if="templateData != null" class="theme-editor-card">
    <b-tabs content-class="theme-editor-pane" class="theme-editor-tabs">
      <b-tab
        v-for="(section, sectionName) in templateData.pages.home.sections"
        :key="sectionName"
        :title="sectionTitle(sectionName)"
        >
        <div v-if="sectionName === 'banner'" class="theme-banner-editor">
            <p class="theme-editor-hint">
              One slide keeps a static banner. Extra slides become a slider on the website.
            </p>
            <div
              v-for="(slideItem, slideKey) in section"
              :key="slideKey"
              class="theme-slide-card"
            >
              <div class="theme-slide-head">
                <h5 class="mb-0">{{ formatSlideLabel(slideKey) }}</h5>
                <button
                  v-if="slideKey !== 'slide_1' && $hasPermission('manage business settings')"
                  type="button"
                  class="btn btn-sm btn-outline-danger"
                  @click="removeBannerSlide(slideKey)"
                >
                  Remove
                </button>
              </div>
              <div class="theme-slide-grid">
                <template v-for="(field, fieldName) in slideItem">
                  <div
                    v-if="field.type === 'image'"
                    :key="slideKey + '-' + fieldName"
                    class="theme-media"
                  >
                    <img
                      v-if="field.path"
                      class="theme-media-image"
                      :src="baseUrl + field.path"
                      alt=""
                    />
                    <div v-else class="theme-media-empty">No image yet</div>
                    <b-form-file
                      class="theme-file mt-2"
                      placeholder="Replace image"
                      browse-text="Replace"
                      drop-placeholder="Drop image here..."
                      name="banner"
                      v-model="field.value"
                      @input="uploadImage(sectionName, slideKey)"
                    ></b-form-file>
                  </div>
                </template>
                <div class="theme-slide-fields">
                  <template v-for="(field, fieldName) in slideItem">
                    <div
                      v-if="field.type === 'text'"
                      :key="slideKey + '-' + fieldName"
                      class="theme-field"
                    >
                      <label>{{ field.label }}</label>
                      <input
                        type="text"
                        class="form-control form-control-solid"
                        :placeholder="field.default"
                        v-model="field.value"
                      />
                    </div>
                  </template>
                </div>
              </div>
            </div>
            <button
              v-if="canAddBannerSlide && $hasPermission('manage business settings')"
              type="button"
              class="btn btn-outline-primary"
              @click="addBannerSlide"
            >
              + Add banner slide
            </button>
        </div>
        <div v-else class="theme-asset-list">
            <div
              v-for="(item, itemName) in section"
              :key="itemName"
              class="theme-asset-card"
              :class="{ 'theme-asset-card-text': item.type === 'text' }"
            >
              <template v-if="item.type === 'image'">
                <label>{{ item.label }}</label>
                <div class="theme-media">
                  <img
                    v-if="item.path"
                    class="theme-media-image"
                    :src="baseUrl + item.path"
                    alt=""
                  />
                  <div v-else class="theme-media-empty">No image yet</div>
                  <b-form-file
                    class="theme-file mt-2"
                    placeholder="Replace image"
                    browse-text="Replace"
                    drop-placeholder="Drop image here..."
                    name="banner"
                    v-model="item.value"
                    @input="uploadImage(sectionName, itemName)"
                  ></b-form-file>
                </div>
              </template>
              <div v-else-if="item.type === 'text'" class="theme-field">
                <label>{{ item.label }}</label>
                <input
                  type="text"
                  class="form-control form-control-solid"
                  :placeholder="item.default"
                  v-model="item.value"
                />
              </div>
            </div>
        </div>
      </b-tab>
    </b-tabs>
    </div>

    <div
      v-if="templateData && templateData.pages && templateData.pages.home"
      class="brand-colors-card mt-6"
      :class="{ 'is-open': brandColorsOpen }"
    >
      <button
        type="button"
        class="brand-colors-toggle"
        :aria-expanded="brandColorsOpen ? 'true' : 'false'"
        @click="brandColorsOpen = !brandColorsOpen"
      >
        <span>
          <span class="brand-colors-title">Brand colours</span>
          <span class="brand-colors-hint">
            Closed by default. Open to change colours. Empty colours use this theme’s own default (not always white).
          </span>
        </span>
        <i
          class="mdi brand-colors-caret"
          :class="brandColorsOpen ? 'mdi-chevron-up' : 'mdi-chevron-down'"
        ></i>
      </button>
      <div v-show="brandColorsOpen" class="brand-colors-body">
        <div class="brand-colors-grid">
          <div
            v-for="field in colorFields"
            :key="field.key"
            class="brand-color-item"
            :class="{ 'is-custom': hasColor(field.key) }"
          >
            <div
              class="brand-color-well"
              :class="{ 'is-auto': !hasColor(field.key) }"
            >
              <span
                class="brand-color-fill"
                :class="{ 'is-auto': !hasColor(field.key) }"
                :style="hasColor(field.key) ? { backgroundColor: colorValue(field.key) } : null"
              ></span>
              <span class="brand-color-dropper" aria-hidden="true">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                  <path d="M12 3l1.8 1.8-8.2 8.2-1.8 5 5-1.8 8.2-8.2L18.8 10 21 7.8 16.2 3 12 3z" fill="currentColor"/>
                  <path d="M4 20h16" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                </svg>
              </span>
              <input
                :ref="'colorPicker_' + field.key"
                type="color"
                class="brand-color-native"
                :value="pickerFallback(field.key)"
                :aria-label="'Choose ' + field.label"
                @input="setColor(field.key, $event.target.value)"
              />
            </div>
            <div class="brand-color-meta">
              <div class="brand-color-name">{{ field.label }}</div>
              <div class="brand-color-value">
                {{ hasColor(field.key) ? colorValue(field.key) : "Theme default" }}
              </div>
              <div class="brand-color-actions">
                <button
                  type="button"
                  class="btn btn-primary btn-sm"
                  @click="openColorPicker(field.key)"
                >
                  Choose colour
                </button>
                <button
                  v-if="hasColor(field.key)"
                  type="button"
                  class="brand-color-clear"
                  @click="removeColor(field.key)"
                >
                  Clear
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="templateData != null"
      class="theme-save-bar"
    >
      <button
        v-if="$hasPermission('manage business settings ') || $hasPermission('manage business settings')"
        type="button"
        class="btn btn-primary"
        @click="saveTemplate()"
      >
        Save changes
      </button>
      <button
        v-if="$hasPermission('manage business settings')"
        type="button"
        class="theme-reset-link"
        @click="resetTheme()"
      >
        Reset to default
      </button>
    </div>

    <!-- Image Upload Modal with Transparency Option -->
    <b-modal 
      id="image-upload-modal" 
      ref="image-upload-modal" 
      title="Image Upload Options" 
      hide-footer
      centered
    >
      <div class="form-group">
        <label>Preview:</label>
        <div class="mb-3">
          <img 
            v-if="imagePreview" 
            :src="imagePreview" 
            alt="Preview" 
            style="max-width: 100%; max-height: 300px; border: 1px solid #ddd; border-radius: 4px;"
          />
        </div>
        <div class="form-check">
          <input 
            class="form-check-input" 
            type="checkbox" 
            id="makeTransparent" 
            v-model="makeImageTransparent"
          />
          <label class="form-check-label" for="makeTransparent">
            Make image transparent
          </label>
        </div>
        <small class="form-text text-muted">
          Check this option to remove the background and make the image transparent.
        </small>
      </div>
      <div class="modal-footer">
        <button type="button" class="btn btn-secondary" @click="cancelUpload" :disabled="isUploading">Cancel</button>
        <button type="button" class="btn btn-primary" @click="confirmUpload" :disabled="isUploading">
          <span v-if="isUploading">
            <b-spinner small class="mr-2"></b-spinner>
            Uploading...
          </span>
          <span v-else>Upload Image</span>
        </button>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { GET_CURRENT_TEMPLATE } from "@/core/services/store/profile.module";
import Swal from "sweetalert2";
import { UPDATE_PERSONAL_INFO } from "@/core/services/store/profile.module";

import {
  SAVE_TEMPLATE,
  UPLOAD_TEMPLATE_IMAGES
} from "@/core/services/store/profile.module";
import {
  GENERAL_SETTINGS,
  GET_GENERAL_SETTINGS
} from "@/core/services/store/config.module";
import { mapGetters } from "vuex";

export default {
  props: {
    template: Number
  },
  name: "TemplateSetting",
  computed: {
    ...mapGetters(["currentUser", "currentUserPersonalInfo"]),
    canAddBannerSlide() {
      if (!this.templateData || !this.templateData.pages || !this.templateData.pages.home) {
        return false;
      }
      const banner = this.templateData.pages.home.sections && this.templateData.pages.home.sections.banner;
      return !!(banner && typeof banner === "object");
    },
    colorFields() {
      return [
        { key: "headerColor", label: "Top bar" },
        { key: "footerColor", label: "Bottom bar" },
        { key: "buttonsColor", label: "Buttons" },
        { key: "backgroundColor", label: "Background" },
        { key: "headerFooterTextColor", label: "Header / footer text" },
        { key: "bodytextColor", label: "Body text" }
      ];
    }
  },
  data() {
    return {
      showHeaderFooterTextColorPicker: false,
      showBodytextColorPicker: false,
      showbuttonsColorPicker: false,
      showHeaderColorPicker: false,
      showFooterColorPicker: false,
      showbackgroundColorPicker:false,
      temp: null,
      templates: [],
      templateData: null,
      selectedFile: null,
      baseUrl: process.env.VUE_APP_API_BASE_URL,
      imagePreview: null,
      makeImageTransparent: false,
      isUploading: false,
      brandColorsOpen: false,
      showDiscoverSection: "1",
      showAdvertisementSection: "1",
      savingHomepageSections: false,
      pendingUpload: {
        sectionName: null,
        itemName: null,
        formData: null,
        file: null
      }
    };
  },
  methods: {
    loadDiscoverSection() {
      this.$store
        .dispatch(GET_GENERAL_SETTINGS)
        .then((resp) => {
          const rows = Array.isArray(resp) ? resp : [];
          let discover = "1";
          let advertisement = "1";
          for (let i = 0; i < rows.length; i++) {
            if (rows[i].key === "show_discover_section") {
              discover = rows[i].value === "0" || rows[i].value === 0 ? "0" : "1";
            }
            if (rows[i].key === "show_advertisement_section") {
              advertisement = rows[i].value === "0" || rows[i].value === 0 ? "0" : "1";
            }
          }
          this.showDiscoverSection = discover;
          this.showAdvertisementSection = advertisement;
        })
        .catch(() => {
          this.showDiscoverSection = "1";
          this.showAdvertisementSection = "1";
        });
    },
    saveHomepageSections() {
      this.savingHomepageSections = true;
      const payload = {
        show_advertisement_section: this.showAdvertisementSection === "0" ? "0" : "1",
      };
      if (Number(this.template) === 12) {
        payload.show_discover_section = this.showDiscoverSection === "0" ? "0" : "1";
      }
      this.$store
        .dispatch(GENERAL_SETTINGS, payload)
        .then((resp) => {
          this.savingHomepageSections = false;
          if (resp && resp.status === "Success") {
            this.makeToast("success", "Section visibility saved");
            return;
          }
          this.makeToast("success", "Section visibility saved");
        })
        .catch((error) => {
          this.savingHomepageSections = false;
          this.makeToast("danger", (error && error.message) || "Could not save");
        });
    },
    saveDiscoverSection() {
      this.saveHomepageSections();
    },
    removeColor(colorKey) {
      // Set the specific color to an empty string
      this.templateData.pages.home[colorKey] = '';
      switch (colorKey) {
        case 'headerColor':
          this.showHeaderColorPicker = false;
          break;
        case 'footerColor':
          this.showFooterColorPicker = false;
          break;
        case 'buttonsColor':
          this.showbuttonsColorPicker = false;
          break;
        case 'backgroundColor':
          this.showbackgroundColorPicker = false;
          break;
        case 'headerFooterTextColor':
          this.showHeaderFooterTextColorPicker = false;
          break;
        case 'bodytextColor':
          this.showBodytextColorPicker = false;
          break;
        default:
          break;
      }
    },
    firstLetterUppercase(title) {
  if (!title) return ''; // Handle empty string

  return title.charAt(0).toUpperCase() + title.slice(1);
},
    sectionTitle(name) {
      const titles = {
        banner: "Home banner",
        sub_section_1: "Promo images",
        sub_section_2: "Extra images",
        advertisment: "Advertisement",
        advertisement: "Advertisement",
        promotion: "Promotions",
        default_image: "Default image",
        about: "About",
        menu: "Menu"
      };
      if (titles[name]) {
        return titles[name];
      }
      return this.firstLetterUppercase(String(name || "").replace(/_/g, " "));
    },
    colorValue(key) {
      if (!this.templateData || !this.templateData.pages || !this.templateData.pages.home) {
        return "";
      }
      return String(this.templateData.pages.home[key] || "").trim();
    },
    hasColor(key) {
      return this.colorValue(key) !== "";
    },
    pickerFallback(key) {
      const value = this.colorValue(key);
      return /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(value) ? value : "#808080";
    },
    openColorPicker(key) {
      const ref = this.$refs["colorPicker_" + key];
      const input = Array.isArray(ref) ? ref[0] : ref;
      if (!input) return;
      if (typeof input.showPicker === "function") {
        input.showPicker();
        return;
      }
      input.click();
    },
    setColor(key, value) {
      if (!this.templateData || !this.templateData.pages || !this.templateData.pages.home) {
        return;
      }
      this.templateData.pages.home[key] = value;
      this.intitilizeColors();
    },
    formatSlideLabel(slideKey) {
      const match = String(slideKey || "").match(/^slide_(\d+)$/i);
      return match ? `Banner slide ${match[1]}` : slideKey;
    },
    createEmptyBannerSlide() {
      return {
        image: {
          label: "Banner Image",
          type: "image",
          value: null,
          path: ""
        },
        heading: {
          label: "Main Heading",
          default: "Your Main Heading",
          type: "text",
          value: ""
        },
        sub_heading: {
          label: "Sub Heading",
          default: "Your Sub Heading",
          type: "text",
          value: ""
        },
        call_to_action: {
          label: "Banner button text",
          default: "Order Now",
          type: "text",
          value: ""
        }
      };
    },
    addBannerSlide() {
      if (!this.canAddBannerSlide) return;
      const banner = this.templateData.pages.home.sections.banner;
      let nextIndex = 1;
      while (banner[`slide_${nextIndex}`]) {
        nextIndex += 1;
      }
      this.$set(banner, `slide_${nextIndex}`, this.createEmptyBannerSlide());
      this.makeToast("success", `Banner slide ${nextIndex} added. Upload an image, then click Save.`);
    },
    removeBannerSlide(slideKey) {
      if (slideKey === "slide_1") return;
      const banner = this.templateData.pages.home.sections.banner;
      if (!banner || !banner[slideKey]) return;
      this.$delete(banner, slideKey);
      this.makeToast("success", "Banner slide removed. Click Save to apply.");
    },
    // Normalize food type name to match directory structure (title case with common exceptions)
    normalizeFoodTypeName(foodTypeName) {
      if (!foodTypeName) return '';
      
      // Special cases that don't follow standard title case (exact folder names)
      const specialCases = {
        'juice bar': 'juice bar',
        'juicebar': 'juice bar',
      };
      
      const lowerInput = foodTypeName.toLowerCase().trim();
      if (specialCases[lowerInput]) {
        return specialCases[lowerInput];
      }
      
      // Words that should stay lowercase in title case (except at start)
      const lowercaseWords = ['and', 'or', 'the', 'a', 'an', 'of', 'in', 'on', 'at', 'to', 'for', 'with', 'by'];
      
      return foodTypeName.split(' ').map((word, index) => {
        const lowerWord = word.toLowerCase();
        // Keep common words lowercase (except if it's the first word)
        if (index !== 0 && lowercaseWords.includes(lowerWord)) {
          return lowerWord;
        }
        // Capitalize first letter of other words
        return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
      }).join(' ');
    },
    makeToast(variant, message) {
      this.$bvToast.toast(message, {
        title: "Message",
        variant: variant,
        solid: true
      });
    },
  async resetTheme() {
  const { value: confirmed } = await Swal.fire({
    title: "Are you sure?",
    text: "You won't be able to revert this!",
    icon: "warning",
    showCancelButton: true,
    confirmButtonColor: "#3085d6",
    cancelButtonColor: "#d33",
    confirmButtonText: "Yes, reset it!"
  });

  if (confirmed) {
    try {
      const templateId = this.currentUserPersonalInfo.template_id;
      const foodTypeName = this.currentUserPersonalInfo.food_type_name;
      
      if (!templateId || !foodTypeName) {
        this.makeToast("danger", "Template ID or Food Type is missing. Cannot reset template.");
        return;
      }

      // Normalize food type name to match directory structure (first letter capitalized)
      const normalizedFoodTypeName = this.normalizeFoodTypeName(foodTypeName);

      // Try to import the default template file
      let templateData = null;
      try {
        // Use the same pattern as loadTemplateData, but with normalized name
        const module = await import(
          "/src/core/config/templates/template" +
          templateId +
          "/" +
          normalizedFoodTypeName +
          "/template.json"
        );
        templateData = module.default;
      } catch (importError) {
        console.error("Error loading template file:", importError);
        // If import fails, show error and return
        const errorMessage = `Template file not found for template ${templateId} and food type "${normalizedFoodTypeName}" (original: "${foodTypeName}"). Please ensure the template file exists.`;
        this.makeToast("danger", errorMessage);
        Swal.fire({
          title: "Error",
          text: errorMessage,
          icon: "error",
          confirmButtonText: "OK"
        });
        return;
      }
      
      if (!templateData) {
        throw new Error(`Template file found but is empty or invalid`);
      }
      
      this.templateData = templateData;
      this.intitilizeColors();
      await this.saveTemplate();
      
      this.makeToast("success", "Template reset to default successfully");
    } catch (error) {
      console.error('Error resetting template:', error);
      
      const errorMessage = `Error resetting template: ${error.message || 'Unknown error'}`;
      this.makeToast("danger", errorMessage);
      
      Swal.fire({
        title: "Error",
        text: errorMessage,
        icon: "error",
        confirmButtonText: "OK"
      });
    }
  }
},
   async saveTemplate(sectionName) {
  //  await this.updateFoodType()
      if (this.currentUser != "") {
        if (!this.templateData) {
          this.makeToast("danger", "Theme is still loading. Please try again in a moment.");
          return;
        }
        const form = {
          templateData: this.templateData,
          business: this.currentUser.id,
          template: this.template,
         // food_type_id:this.food_type
        };

        const savePromise = this.$store.dispatch(SAVE_TEMPLATE, form).then(data => {
          this.templateData = data.data;
          this.makeToast("success", "Business Template updated successfully");
          return data;
        });
        let personalInfo = JSON.parse(sessionStorage.getItem('personalInfo'));
        personalInfo.template_id = this.template;
        sessionStorage.setItem('personalInfo', JSON.stringify(personalInfo));
        this.$store.dispatch(UPDATE_PERSONAL_INFO)

        return savePromise;
      }
    },
    handleFileChange(event) {
      this.templateData["pages"]["home"]["sections"]["banner"]["slide_1"][
        "image"
      ]["value"] = event.target.files[0];
    },
  
    uploadImage(sectionName, itemName) {
  if (sectionName === "banner") {
    this.selectedFile = this.templateData.pages.home.sections[sectionName][itemName].image.value;
  } else {
    this.selectedFile = this.templateData.pages.home.sections[sectionName][itemName].value;
  }

  if (!this.selectedFile) {
    return;
  }

  const file = this.selectedFile;

  // Create preview
  const reader = new FileReader();
  reader.onload = (e) => {
    this.imagePreview = e.target.result;
    
    // Store upload information for later
    this.pendingUpload.sectionName = sectionName;
    this.pendingUpload.itemName = itemName;
    this.pendingUpload.file = file;
    
    // Show modal
    this.$refs["image-upload-modal"].show();
  };
  reader.readAsDataURL(file);
},
confirmUpload() {
  const { sectionName, itemName, file } = this.pendingUpload;
  
  if (!file) {
    this.$refs["image-upload-modal"].hide();
    return;
  }

  // Set loading state
  this.isUploading = true;

  // Reset the file input immediately to prevent v-model issues
  const sectionNameForReset = sectionName;
  const itemNameForReset = itemName;
  if (sectionNameForReset === "banner") {
    this.$set(this.templateData.pages.home.sections[sectionNameForReset][itemNameForReset].image, 'value', null);
  } else {
    this.$set(this.templateData.pages.home.sections[sectionNameForReset][itemNameForReset], 'value', null);
  }

  const formData = new FormData();
  formData.append("image", file);
  formData.append("image_key", itemName);
  formData.append("image_section", sectionName);
  formData.append("template_id", this.template);
  formData.append("user_id", this.currentUser.id);
  formData.append("make_transparent", this.makeImageTransparent ? "1" : "0");

  // Conditional check for specific sections
  if (
    sectionName === "promotion" ||
    sectionName === "sub_section_1" ||
    sectionName === "sub_section_2"
  ) {
    const maxSize = 5 * 1024 * 1024; // 5MB
    const minWidth = 200; // Minimum width: 200px
    const minHeight = 200; // Minimum height: 300px
    const maxWidth = 500; // Maximum width: 500px
    const maxHeight = 750; // Maximum height: 750px

      // Check file size
    if (file.size > maxSize) {
      this.makeToast("danger", "File size exceeds the maximum limit of 5MB.");
      this.$refs["image-upload-modal"].hide();
      this.isUploading = false;
      return;
    }

    const img = new Image();
    img.onload = () => {
      const width = img.width;
      const height = img.height;

      // Check minimum dimensions
      if (width < minWidth || height < minHeight) {
        this.makeToast(
          "danger",
          `Image dimensions are too small. Minimum dimensions are ${minWidth}x${minHeight} pixels.`
        );
        this.$refs["image-upload-modal"].hide();
        this.isUploading = false;
        return;
      }

      // Check maximum dimensions
      if (width > maxWidth || height > maxHeight) {
        this.makeToast(
          "danger",
          `Image dimensions are too large. Maximum dimensions are ${maxWidth}x${maxHeight} pixels.`
        );
        this.$refs["image-upload-modal"].hide();
        this.isUploading = false;
        return;
      }

      // Proceed with the upload if checks pass
      this.$store.dispatch(UPLOAD_TEMPLATE_IMAGES, formData).then((response) => {
        // Handle response structure: { status, message, data: { image_path, image_url } }
        const imagePath = (response.data && response.data.image_path) 
          ? response.data.image_path 
          : (response.data && response.data.image_url) 
            ? response.data.image_url 
            : (response.data) 
              ? response.data 
              : response;
        if (sectionName === "banner") {
          this.templateData.pages.home.sections[sectionName][itemName].image.path = imagePath;
          // Reset the file input using $set for reactivity
          this.$set(this.templateData.pages.home.sections[sectionName][itemName].image, 'value', null);
        } else {
          this.templateData.pages.home.sections[sectionName][itemName].path = imagePath;
          // Reset the file input using $set for reactivity
          this.$set(this.templateData.pages.home.sections[sectionName][itemName], 'value', null);
        }
        this.isUploading = false;
        this.$refs["image-upload-modal"].hide();
        this.resetUploadModal();
      }).catch((error) => {
        console.error("Upload error:", error);
        this.isUploading = false;
        this.makeToast("danger", "Failed to upload image. Please try again.");
      });
    };

    img.onerror = () => {
      this.makeToast("danger", "Invalid image file.");
      this.isUploading = false;
      this.$refs["image-upload-modal"].hide();
    };

    img.src = URL.createObjectURL(file);
  } else {
    // If no dimension or size checks are needed, proceed to upload
    this.$store.dispatch(UPLOAD_TEMPLATE_IMAGES, formData).then((response) => {
      // Handle response structure: { status, message, data: { image_path, image_url } }
      const imagePath = (response.data && response.data.image_path) 
        ? response.data.image_path 
        : (response.data && response.data.image_url) 
          ? response.data.image_url 
          : (response.data) 
            ? response.data 
            : response;
      if (sectionName === "banner") {
        this.templateData.pages.home.sections[sectionName][itemName].image.path = imagePath;
        // Reset the file input using $set for reactivity
        this.$set(this.templateData.pages.home.sections[sectionName][itemName].image, 'value', null);
      } else {
        this.templateData.pages.home.sections[sectionName][itemName].path = imagePath;
        // Reset the file input using $set for reactivity
        this.$set(this.templateData.pages.home.sections[sectionName][itemName], 'value', null);
      }
      this.isUploading = false;
      this.$refs["image-upload-modal"].hide();
      this.resetUploadModal();
    }).catch((error) => {
      console.error("Upload error:", error);
      this.isUploading = false;
      this.makeToast("danger", "Failed to upload image. Please try again.");
    });
  }
},
cancelUpload() {
  // Reset file input - use Vue.set or direct assignment to ensure reactivity
  const sectionName = this.pendingUpload.sectionName;
  const itemName = this.pendingUpload.itemName;
  
  if (sectionName && itemName) {
    if (sectionName === "banner") {
      this.$set(this.templateData.pages.home.sections[sectionName][itemName].image, 'value', null);
    } else {
      this.$set(this.templateData.pages.home.sections[sectionName][itemName], 'value', null);
    }
  }
  
  this.$refs["image-upload-modal"].hide();
  this.resetUploadModal();
},
resetUploadModal() {
  this.imagePreview = null;
  this.makeImageTransparent = false;
  this.isUploading = false;
  this.pendingUpload = {
    sectionName: null,
    itemName: null,
    formData: null,
    file: null
  };
},
    intitilizeColors() {
      this.showbuttonsColorPicker = !!this.templateData.pages.home.buttonsColor;
      this.showHeaderColorPicker = !!this.templateData.pages.home.headerColor;
      this.showFooterColorPicker = !!this.templateData.pages.home.footerColor;
      this.showbackgroundColorPicker = !!this.templateData.pages.home.backgroundColor;
      this.showHeaderFooterTextColorPicker = !!this.templateData.pages.home.headerFooterTextColor;
      this.showBodytextColorPicker = !!this.templateData.pages.home.bodytextColor;
    },

    loadTemplateData(template_id) {
      

      this.$store
        .dispatch(GET_CURRENT_TEMPLATE, { template_id: template_id })
        .then(result => {
          
          if (result.data.template_data) {
            this.templateData = result.data.template_data;
              this.intitilizeColors()
          } else {
                // Normalize food type name to match directory structure
                const normalizedFoodTypeName = this.normalizeFoodTypeName(this.currentUserPersonalInfo.food_type_name);
                
                import(
                  "/src/core/config/templates/template" +
                  template_id +
                  "/" +
                  normalizedFoodTypeName +
                  "/template.json"
              )
                .then(module => {
                  this.templateData = module.default;
                  this.intitilizeColors()


                })
                .catch(error => {
                  console.error("Error loading JSON file:", error);
                  this.makeToast("danger", `Failed to load template file for "${normalizedFoodTypeName}". Please check if the template file exists.`);
                });
            
          }
        });
    }
  },
  mounted() {
    this.loadTemplateData(this.template);
    this.loadDiscoverSection();
  },
  watch: {
    template(newVal, oldVal) {
      this.loadTemplateData(newVal);
      this.loadDiscoverSection();
    }
  }
};
</script>
<style scoped>

.border-danger {
  border-color: red !important; /* Change to your preferred error color */
}
.text-danger {
  color: red; /* Error message color */
}
.brand-colors-card {
  padding: 4px 18px 10px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
}
.brand-colors-toggle {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
  padding: 14px 0 10px;
  border: 0;
  background: transparent;
  text-align: left;
  cursor: pointer;
}
.brand-colors-title {
  display: block;
  font-size: 15px;
  font-weight: 700;
  color: #241b5a;
}
.brand-colors-hint {
  display: block;
  margin-top: 4px;
  font-size: 12px;
  color: #64748b;
  max-width: 760px;
  line-height: 1.45;
}
.brand-colors-caret {
  font-size: 22px;
  line-height: 1;
  color: #241b5a;
  margin-top: 2px;
}
.brand-colors-body {
  padding-top: 4px;
  padding-bottom: 8px;
}
.brand-colors-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 12px;
  margin-top: 8px;
}
.brand-color-item {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
}
.brand-color-item.is-custom {
  border-color: #c7d2fe;
}
.brand-color-well {
  position: relative;
  width: 64px;
  height: 64px;
  flex-shrink: 0;
  padding: 0;
  border: 2px solid #241b5a;
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.12);
  overflow: hidden;
}
.brand-color-well.is-auto {
  padding: 3px;
  border: 0;
  background: conic-gradient(#ef4444, #f59e0b, #22c55e, #3b82f6, #a855f7, #ef4444);
}
.brand-color-well:hover {
  transform: translateY(-1px);
  box-shadow: 0 6px 14px rgba(36, 27, 90, 0.16);
}
.brand-color-fill {
  display: block;
  width: 100%;
  height: 100%;
  border-radius: 8px;
  background: #fff;
  pointer-events: none;
}
.brand-color-fill.is-auto {
  background-color: #f8fafc;
  background-image:
    linear-gradient(45deg, #cbd5e1 25%, transparent 25%),
    linear-gradient(-45deg, #cbd5e1 25%, transparent 25%),
    linear-gradient(45deg, transparent 75%, #cbd5e1 75%),
    linear-gradient(-45deg, transparent 75%, #cbd5e1 75%);
  background-size: 10px 10px;
  background-position: 0 0, 0 5px, 5px -5px, -5px 0;
}
.brand-color-dropper {
  position: absolute;
  right: 5px;
  bottom: 5px;
  width: 22px;
  height: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  background: #241b5a;
  color: #fff;
  pointer-events: none;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.25);
  z-index: 1;
}
.brand-color-native {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  cursor: pointer;
  border: 0;
  padding: 0;
}
.brand-color-meta {
  min-width: 0;
  flex: 1;
}
.brand-color-name {
  font-size: 14px;
  font-weight: 700;
  color: #0f172a;
}
.brand-color-value {
  margin: 2px 0 8px;
  font-size: 12px;
  color: #64748b;
  word-break: break-word;
}
.brand-color-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}
.brand-color-clear {
  border: 0;
  background: transparent;
  padding: 0;
  color: #be123c;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
}
.theme-customise {
  padding-bottom: 4.5rem;
}
.theme-editor-card {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 12px 20px 20px;
}
.theme-editor-tabs ::v-deep .nav-tabs {
  border-bottom: 1px solid #e2e8f0;
  margin-bottom: 0;
}
.theme-editor-tabs ::v-deep .nav-tabs .nav-link {
  border: 0;
  color: #64748b;
  font-weight: 700;
  padding: 14px 16px 12px;
}
.theme-editor-tabs ::v-deep .nav-tabs .nav-link.active {
  color: #241b5a;
  box-shadow: inset 0 -2px 0 #241b5a;
  background: transparent;
}
.theme-editor-tabs ::v-deep .tab-content {
  padding-top: 18px;
}
.theme-editor-pane {
  padding-top: 4px;
}
.theme-editor-hint {
  color: #475569;
  font-size: 13px;
  line-height: 1.5;
  margin: 0 0 20px;
  padding: 12px 14px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
}
.theme-slide-card {
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 16px;
  background: #f8fafc;
}
.theme-slide-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
}
.theme-slide-head h5 {
  font-size: 15px;
  font-weight: 700;
  color: #0f172a;
}
.theme-slide-grid {
  display: grid;
  grid-template-columns: minmax(240px, 1.1fr) minmax(220px, 1fr);
  gap: 18px;
  align-items: start;
}
.theme-media-image {
  width: 100%;
  height: 210px;
  object-fit: cover;
  border-radius: 10px;
  display: block;
  background: #e2e8f0;
}
.theme-media-empty {
  height: 160px;
  border-radius: 10px;
  background: #eef2ff;
  color: #64748b;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 600;
}
.theme-slide-fields .theme-field + .theme-field {
  margin-top: 12px;
}
.theme-field label {
  display: block;
  font-size: 12px;
  font-weight: 700;
  color: #334155;
  margin-bottom: 6px;
}
.theme-asset-list {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 16px;
}
.theme-asset-card {
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 12px;
  background: #f8fafc;
}
.theme-asset-card > label {
  display: block;
  font-size: 12px;
  font-weight: 700;
  color: #334155;
  margin-bottom: 8px;
}
.theme-asset-card-text {
  grid-column: 1 / -1;
}
.theme-save-bar {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #e2e8f0;
  flex-wrap: wrap;
}
.theme-reset-link {
  border: 0;
  background: transparent;
  color: #be123c;
  font-size: 13px;
  font-weight: 700;
  padding: 0;
  cursor: pointer;
}
@media (max-width: 767px) {
  .theme-slide-grid {
    grid-template-columns: 1fr;
  }
}
</style>